import { useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash/fp';
import { LayoutDirection } from 'app/lib/constants';
import { useSelector, useDispatch } from 'app/lib/hooks';
import { Field, useFormContext } from 'app/components/core/Form';
import { Input } from 'app/components/core/Input';
import { Fieldset } from 'app/components/core/Fieldset';
import { Textarea } from 'app/components/core/Textarea';
import { ComboBox } from 'app/components/core/ComboBox';
import { ModalErrorBanner } from 'app/components/core/Modal';
import {
    DialogChapter,
    KeyValueSummary,
    SummaryHint
} from 'app/components/features/DialogWithChapters';
import { selectUserCanManageJobs } from 'app/store/users';
import {
    doListEnvironmentRoles,
    doListEnvironmentWarehouses,
    selectIsLoadingEnvironmentRoles,
    selectEnvironmentRoles,
    selectIsLoadingEnvironmentWarehouses,
    selectEnvironmentWarehouses,
    selectEnvironmentRolesError
} from 'app/store/projectModels';

function useResetValues() {
    const dispatch = useDispatch();
    const { getFieldValue, setFieldValue, isPristine, pristine } = useFormContext();
    const serviceAccountUid = getFieldValue('account.serviceAccountUid');
    const user = getFieldValue('account.credentials.user');
    const haveRequiredFieldsChanged =
        !isPristine &&
        (pristine?.account?.credentials?.user === false ||
            pristine?.account?.serviceAccountUid === false);

    useEffect(() => {
        if (haveRequiredFieldsChanged) {
            setFieldValue('attributes.role', null);
            setFieldValue('attributes.warehouse', null);
        }
    }, [setFieldValue, dispatch, haveRequiredFieldsChanged, serviceAccountUid, user]);
}

export function EnvironmentAttributesField() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { getFieldValue } = useFormContext();
    const attributes = getFieldValue('attributes');
    const role = getFieldValue('attributes.role.name');
    const serviceAccountUid = getFieldValue('account.serviceAccountUid');
    const isAdmin = useSelector(selectUserCanManageJobs);
    const isLoadingRoles = useSelector(selectIsLoadingEnvironmentRoles);
    const roles = useSelector(selectEnvironmentRoles);
    const isLoadingWarehouses = useSelector(selectIsLoadingEnvironmentWarehouses);
    const warehouses = useSelector(selectEnvironmentWarehouses);
    const error = useSelector(selectEnvironmentRolesError);
    const isChapterEnabled = !isEmpty(serviceAccountUid);

    useResetValues();

    const handleRoleChange = useCallback(
        (value, { setFieldValue, resetField }) => {
            setFieldValue('role', value.name);
            resetField('attributes.warehouse');
            setFieldValue('attributes.warehouse', null);
            const params = { serviceAccountUid, role: value.name };
            dispatch(doListEnvironmentWarehouses(params));
        },
        [dispatch, serviceAccountUid]
    );

    const summary = useMemo(() => {
        const values = {
            [t('glossary:role')]: attributes?.role?.name,
            [t('glossary:warehouse')]: attributes?.warehouse?.name,
            [t('common:name')]: attributes?.name
        };

        return !isChapterEnabled ? (
            <SummaryHint>{t('jobs:dialogs.environment.invalidIdentity')}</SummaryHint>
        ) : (
            <KeyValueSummary values={values} />
        );
    }, [t, isChapterEnabled, attributes]);

    const handleOpenChapter = useCallback(() => {
        const params = { serviceAccountUid };
        dispatch(doListEnvironmentRoles(params));
        if (role) {
            dispatch(
                doListEnvironmentWarehouses({
                    ...params,
                    role
                })
            );
        }
    }, [dispatch, role, serviceAccountUid]);

    return (
        <DialogChapter
            label={t('jobs:dialogs.environment.attributes')}
            summary={summary}
            onOpen={handleOpenChapter}
            disabled={!isChapterEnabled}>
            {error && <ModalErrorBanner error={error} />}
            <Fieldset direction={LayoutDirection.ROW}>
                <Field
                    component={ComboBox}
                    name="attributes.role"
                    label={t('glossary:role')}
                    placeholder={t('jobs:dialogs.environment.selectRole')}
                    disabled={!isAdmin}
                    options={roles}
                    isProcessing={isLoadingRoles}
                    onChange={handleRoleChange}
                />

                <Field
                    component={ComboBox}
                    name="attributes.warehouse"
                    label={t('glossary:warehouse')}
                    placeholder={t('jobs:dialogs.environment.selectWarehouse')}
                    options={warehouses}
                    isProcessing={isLoadingWarehouses}
                    disabled={!role}
                />
            </Fieldset>
            <Field
                component={Input}
                name="attributes.name"
                label={t('common:name')}
                placeholder={t('jobs:dialogs.environment.namePlaceholder')}
            />
            <Field
                component={Textarea}
                name="attributes.description"
                maxChars={400}
                label={t('common:description_optional')}
                placeholder={t('jobs:dialogs.environment.descriptionPlaceholder')}
            />
        </DialogChapter>
    );
}
